"use client";

import {
  LandingSiteTemplateGetDataOutputApiModel,
  SiteTemplatePageSectionTypeBannerSettingModel,
} from "@/Apis/ClinicApi";
import { SmartImage, SmartLink } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { useAppSetting, useBodyClass, useHtmlClass, useT } from "@/Hooks";
import { getSectionDefaultProps } from "@/Templates/Doccure/getSectionDefaultProps";
import { HeaderSectionProps } from "@/Templates/SectionsProps";
import _ from "lodash";
import {
  FC,
  cloneElement,
  isValidElement,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import HomeEighteenHeaderSectionGenerated, {
  HomeEighteenHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenHeaderSection.generated";
import HomeEightHeaderEightGenerated, {
  HomeEightHeaderEightGeneratedProps,
} from "../Sections/_generated/HomeEightHeaderEight.generated";
import HomeElevenHeaderSectionGenerated, {
  HomeElevenHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeElevenHeaderSection.generated";
import HomeFiveHeaderSectionGenerated, {
  HomeFiveHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeFiveHeaderSection.generated";
import HomeFourHeaderSectionGenerated, {
  HomeFourHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeFourHeaderSection.generated";
import HomeFourteenHeaderSectionGenerated, {
  HomeFourteenHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenHeaderSection.generated";
import HomeNineHeaderSectionGenerated, {
  HomeNineHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeNineHeaderSection.generated";
import HomeOneHeaderOneGenerated, {
  HomeOneHeaderOneGeneratedProps,
} from "../Sections/_generated/HomeOneHeaderOne.generated";
import HomeSevenHeaderSectionGenerated, {
  HomeSevenHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeSevenHeaderSection.generated";
import HomeSeventeenHeaderSectionGenerated, {
  HomeSeventeenHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenHeaderSection.generated";
import HomeSixHeaderSectionGenerated, {
  HomeSixHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeSixHeaderSection.generated";
import HomeSixteenHeaderSectionGenerated, {
  HomeSixteenHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeSixteenHeaderSection.generated";
import HomeTenHeaderSectionGenerated, {
  HomeTenHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeTenHeaderSection.generated";
import HomeThirteenHeaderSectionGenerated, {
  HomeThirteenHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeThirteenHeaderSection.generated";
import HomeThreeHeaderSectionGenerated, {
  HomeThreeHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeThreeHeaderSection.generated";
import HomeTwelveHeaderSectionGenerated, {
  HomeTwelveHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeTwelveHeaderSection.generated";
import HomeTwoHeaderSectionGenerated, {
  HomeTwoHeaderSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoHeaderSection.generated";
import {
  GeneratedSectionRenderer,
  renderGeneratedSection,
} from "./generated-section-renderer";
import HeaderAccountActions from "./HeaderAccountActions";
import SocialNetworks from "./SocialNetworks";
import useDoccureVideoModal from "./useDoccureVideoModal";

type HeaderRenderContext = {
  pageData: LandingSiteTemplateGetDataOutputApiModel;
  isScrolled: boolean;
  onMobileBtn: MouseEventHandler<HTMLAnchorElement>;
  onMenuCloseAnchor: MouseEventHandler<HTMLAnchorElement>;
  onMenuCloseButton: MouseEventHandler<HTMLButtonElement>;
  signInButtons: ReactNode;
  signInButtonsCompact: ReactNode;
  logo: ReactNode;
  logoWithName: ReactNode;
  renderLogo: (className?: string) => ReactNode;
  contactInformationText: string;
  followUsText: string;
  loginText: string;
  emailText: string;
  mobileText: string;
  contactUsText: string;
  signInHref: string;
};

const HeaderVideoLinkRenderer: FC<{
  src: string;
  defaultNode: ReactNode;
}> = ({ src, defaultNode }) => {
  const videoModal = useDoccureVideoModal();

  if (!isValidElement(defaultNode)) {
    return defaultNode;
  }

  const linkNode = defaultNode as ReactElement<{
    className?: string;
    children?: ReactNode;
  }>;

  return (
    <div
      className={linkNode.props.className}
      role="button"
      tabIndex={0}
      onClick={() => videoModal.show(src)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          videoModal.show(src);
        }
      }}
    >
      {linkNode.props.children}
    </div>
  );
};

type GeneratedHeaderRenderer = (
  props: HeaderSectionProps,
  context: HeaderRenderContext,
) => ReactElement;

const HeaderAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateHeaderAppearance;
const HeroAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateHeroAppearance;

function buildHeaderSupportItems(
  context: HeaderRenderContext,
  valueKey: "h30Text" | "h40Text" | "h50Text" | "pText",
  classNameKey?: "className" | "i0ClassName",
): Array<Record<string, unknown>> {
  return [
    ...(context.pageData.CommonData.Email
      ? [
          {
            key: 1,
            className: "support-item mb-3",
            [classNameKey || "className"]: "isax isax-messages-3 me-1",
            p0Text: context.emailText,
            [valueKey]: context.pageData.CommonData.Email,
          },
        ]
      : []),
    ...(context.pageData.CommonData.Tels?.[0]
      ? [
          {
            key: 2,
            className: "support-item",
            [classNameKey || "className"]: "isax isax-call-calling me-1",
            p0Text: context.mobileText,
            [valueKey]: context.pageData.CommonData.Tels[0],
          },
        ]
      : []),
  ];
}

function applySharedHeaderTextOverrides<T extends Record<string, unknown>>(
  generatedProps: T,
  context: HeaderRenderContext,
): T {
  const nextProps: Record<string, unknown> = {
    ...generatedProps,
  };

  for (const [key, value] of Object.entries(nextProps)) {
    if (typeof value !== "string") {
      continue;
    }

    if (value === "Contact Information") {
      nextProps[key] = context.contactInformationText;
      continue;
    }

    if (value === "Follow Us") {
      nextProps[key] = context.followUsText;
      continue;
    }

    if (value === "Sign In" || value === "Sign") {
      nextProps[key] = context.loginText;
      continue;
    }

    if (value === "all_routes.login" || value === "route.login") {
      nextProps[key] = context.signInHref;
    }
  }

  return nextProps as T;
}

function renderHeaderSocialNetworks(
  context: HeaderRenderContext,
  options?: {
    wrapWithLi?: boolean;
  },
): ReactNode {
  const socialNetworks = context.pageData.CommonData.SocialNetworks;

  if (!socialNetworks?.length) {
    return undefined;
  }

  return (
    <SocialNetworks
      socialNetworks={socialNetworks}
      wrapWithLi={options?.wrapWithLi}
    />
  );
}

function buildGeneratedHeaderProps<T extends Record<string, unknown>>(
  sectionKey: string,
  props: HeaderSectionProps,
  context: HeaderRenderContext,
  overrides?: Partial<T>,
): T {
  const baseOverrides: Record<string, unknown> = {
    menus: props.pageData.Menus,
    ...(overrides ?? {}),
  };

  return applySharedHeaderTextOverrides(
    getSectionDefaultProps<T>(sectionKey, {
      ...baseOverrides,
    } as unknown as Partial<T>),
    context,
  );
}

function isActiveHeaderOption(option?: { Key?: string }): boolean | undefined {
  if (!option) {
    return undefined;
  }

  return option.Key === CenterOptions.Enum.Status.Active.key;
}

function getHeaderFileUrl(
  file?: { Url?: string } | string,
): string | undefined {
  return typeof file === "string" ? file || undefined : file?.Url || undefined;
}

function getBannerSectionSetting(
  pageData: LandingSiteTemplateGetDataOutputApiModel,
): SiteTemplatePageSectionTypeBannerSettingModel | undefined {
  const bannerSection = pageData.Sections.find(
    (section) =>
      section.CenterSiteTemplatePageSectionTypeKey === "Banner" ||
      section.Key === "Banner",
  );

  if (!bannerSection?.Setting || typeof bannerSection.Setting !== "object") {
    return undefined;
  }

  return bannerSection.Setting as SiteTemplatePageSectionTypeBannerSettingModel;
}

function createHeaderTitleRenderer(title?: string) {
  if (!title?.trim()) {
    return undefined;
  }

  return (defaultNode: ReactNode) =>
    isValidElement(defaultNode)
      ? cloneElement(defaultNode, undefined, title)
      : title;
}

const generatedHeaderRenderers: Record<string, GeneratedHeaderRenderer> = {
  [HeaderAppearance.Home1.key]: (props, context) => (
    <HomeOneHeaderOneGenerated
      {...buildGeneratedHeaderProps<HomeOneHeaderOneGeneratedProps>(
        "HomeOneHeaderOne",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          headerDiv0Nav0Div1Div0Div1Div2Link1LinkVisible: false,
          headerOneUls3Renderer: (_) => <li>{context.signInButtonsCompact}</li>,
          headerOneSupport3s: buildHeaderSupportItems(
            context,
            "h50Text",
            "i0ClassName",
          ) as unknown as HomeOneHeaderOneGeneratedProps["headerOneSupport3s"],
          headerOneDivs: [
            {
              key: 1,
              className: "isax isax-message-text5 me-1",
              pText: context.pageData.CommonData.Email,
            },
            ...context.pageData.CommonData.Tels?.map((tel, index) => ({
              key: index + 2,
              className: "isax isax-call5 me-1",
              pText: tel,
            })),
          ],
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.logoWithName,
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Div0Link0LinkHref: "/",
          headerDiv0Nav0Div1Div0Div0Link0LinkRenderer: () => (
            <SmartLink href="/" className="menu-logo">
              {context.logoWithName}
            </SmartLink>
          ),
          headerDiv0Nav0Div1Div0Div1Div0H30H3Renderer: () => (
            <h3 className="title">{context.contactInformationText}</h3>
          ),
          headerDiv0Nav0Div1Div0Div1Div1H30H3Renderer: () => (
            <h3 className="title">{context.followUsText}</h3>
          ),
          headerOneDivs2Renderer: (_) => renderHeaderSocialNetworks(context),
          headerOneUls2Renderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: false }),
        },
      )}
    />
  ),
  [HeaderAppearance.Home2.key]: (props, context) => (
    <HomeTwoHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeTwoHeaderSectionGeneratedProps>(
        "HomeTwoHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          headerDiv0Nav0Div1Div1Div2Link1LinkVisible: false,
          headerDiv0Nav0Div1Div1Div2Link0LinkRenderer: (
            _defaultNode: ReactNode,
          ) => context.signInButtons,
          headerSectionUls2Renderer: (_defaultNode: ReactNode) =>
            context.signInButtonsCompact,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid white-logo"),
          headerDiv0Nav0Div0Link1ImageWithBasePath1ImageRenderer: (_) =>
            context.renderLogo("img-fluid normal-logo"),
          headerDiv0Nav0Div1Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Link0LinkHref: "/",
          headerSectionSupport3s: buildHeaderSupportItems(
            context,
            "h50Text",
          ) as unknown as HomeTwoHeaderSectionGeneratedProps["headerSectionSupport3s"],
          headerSectionUlsRenderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
        },
      )}
    />
  ),
  [HeaderAppearance.Home3.key]: (props, context) => (
    <HomeThreeHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeThreeHeaderSectionGeneratedProps>(
        "HomeThreeHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div1Div0Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Div0Link0LinkHref: "/",
          headerDiv0Nav0Div1Div0Div1Div2Link1LinkVisible: false,
          headerDiv0Nav0Div1Div0Div1Div2Link0LinkRenderer: (
            _defaultNode: ReactNode,
          ) => context.signInButtons,
          headerSectionSupport3s: buildHeaderSupportItems(
            context,
            "h50Text",
          ) as unknown as HomeThreeHeaderSectionGeneratedProps["headerSectionSupport3s"],
          headerSectionUlsRenderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
          headerDiv0Nav0Ul0Li2Link0LinkRenderer: (_defaultNode: ReactNode) =>
            context.signInButtonsCompact,
        },
      )}
    />
  ),
  [HeaderAppearance.Home4.key]: (props, context) => (
    <HomeFourHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeFourHeaderSectionGeneratedProps>(
        "HomeFourHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          divHeader0Div0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          divHeader0Div0Nav0Div1Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          divHeader0Div0Nav0Div0Link1LinkHref: "/",
          divHeader0Div0Nav0Div1Div0Link0LinkHref: "/",
          divHeader0Div0Nav0Div1Div1Div2Link1LinkVisible: false,
          divHeader0Div0Nav0Div1Div1Div2Link0LinkRenderer: (
            _defaultNode: ReactNode,
          ) => context.signInButtons,
          divHeader0Div0Nav0Ul0Li2Link0LinkRenderer: (_defaultNode: ReactNode) =>
            context.signInButtonsCompact,
          headerSectionDivs: buildHeaderSupportItems(
            context,
            "pText",
          ) as unknown as HomeFourHeaderSectionGeneratedProps["headerSectionDivs"],
          headerSectionDivs2Renderer: (_) =>
            renderHeaderSocialNetworks(context),
          headerSectionUls2Renderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
        },
      )}
    />
  ),
  [HeaderAppearance.Home5.key]: (props, context) => (
    <HomeFiveHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeFiveHeaderSectionGeneratedProps>(
        "HomeFiveHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div1Div0Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Div0Link0LinkHref: "/",
          headerDiv0Nav0Div1Div0Div1Div2Link1LinkVisible: false,
          headerDiv0Nav0Div1Div0Div1Div2Link0LinkRenderer: (
            _defaultNode: ReactNode,
          ) => context.signInButtons,
          headerSectionSupport3s: buildHeaderSupportItems(
            context,
            "h50Text",
          ) as unknown as HomeFiveHeaderSectionGeneratedProps["headerSectionSupport3s"],
          headerSectionUlsRenderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
          headerDiv0Nav0Ul0Li0Div0Div1Span0SpanText: context.contactUsText,
          headerDiv0Nav0Ul0Li0Div0Div1P0Link0LinkText:
            context.pageData.CommonData.Tels?.[0] ?? "",
          headerDiv0Nav0Ul0Li0Div0Div1P0Link0LinkHref: context.pageData
            .CommonData.Tels?.[0]
            ? `tel:${context.pageData.CommonData.Tels?.[0]}`
            : "#",
          headerDiv0Nav0Ul0Li1Link0LinkRenderer: (_) => context.signInButtons,
        },
      )}
    />
  ),
  [HeaderAppearance.Home6.key]: (props, context) => (
    <HomeSixHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeSixHeaderSectionGeneratedProps>(
        "HomeSixHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div1Div0Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Div0Link0LinkHref: "/",
          headerDiv0Nav0Div1Div0Div1Div2Link1LinkVisible: false,
          headerDiv0Nav0Div1Div0Div1Div2Link0LinkRenderer: (
            _defaultNode: ReactNode,
          ) => context.signInButtons,
          headerSectionSupport3s: buildHeaderSupportItems(
            context,
            "h50Text",
          ) as unknown as HomeSixHeaderSectionGeneratedProps["headerSectionSupport3s"],
          headerSectionUlsRenderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
          headerDiv0Nav0Ul0Li0Link0LinkRenderer: (_) => context.signInButtons,
        },
      )}
    />
  ),
  [HeaderAppearance.Home7.key]: (props, context) => (
    <HomeSevenHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeSevenHeaderSectionGeneratedProps>(
        "HomeSevenHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          divHeader0Div0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid white-logo"),
          divHeader0Div0Nav0Div0Link1ImageWithBasePath1ImageRenderer: (_) =>
            context.renderLogo("img-fluid normal-logo"),
          divHeader0Div0Nav0Div1Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerSectionUlsRenderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
          divHeader0Div0Nav0Ul0Li1Link0LinkRenderer: (_) =>
            context.signInButtonsCompact,
          headerSectionDivs: buildHeaderSupportItems(
            context,
            "pText",
          ) as unknown as HomeSevenHeaderSectionGeneratedProps["headerSectionDivs"],
        },
      )}
    />
  ),
  [HeaderAppearance.Home8.key]: (props, context) => (
    <HomeEightHeaderEightGenerated
      {...buildGeneratedHeaderProps<HomeEightHeaderEightGeneratedProps>(
        "HomeEightHeaderEight",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div1Div0Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Div0Link0LinkHref: "/",
          headerDiv0Nav0Div1Div0Div1Div2Link1LinkVisible: false,
          headerDiv0Nav0Div1Div0Div1Div2Link0LinkRenderer: (
            _defaultNode: ReactNode,
          ) => context.signInButtons,
          headerEightSupport3s: buildHeaderSupportItems(
            context,
            "h50Text",
          ) as unknown as HomeEightHeaderEightGeneratedProps["headerEightSupport3s"],
          headerEightUlsRenderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
          headerDiv0Nav0Ul0Li1Link0LinkRenderer: (_) =>
            context.signInButtonsCompact,
          headerDiv0Nav0Ul0Li0Div0P0Link0LinkText:
            context.pageData.CommonData.Tels?.[0] ?? "",
          headerDiv0Nav0Ul0Li0Div0P0Link0LinkHref: context.pageData.CommonData
            .Tels?.[0]
            ? `tel:${context.pageData.CommonData.Tels?.[0]}`
            : "#",
        },
      )}
    />
  ),
  [HeaderAppearance.Home9.key]: (props, context) => (
    <HomeNineHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeNineHeaderSectionGeneratedProps>(
        "HomeNineHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link2ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div1Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div1Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div0Link2LinkHref: "/",
          headerDiv0Nav0Div1Div0Link0LinkHref: "/",
          headerDiv0Nav0Div1Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div1Div2Link1LinkVisible: false,
          headerDiv0Nav0Div1Div1Div2Link0LinkRenderer: (
            _defaultNode: ReactNode,
          ) => context.signInButtons,
          headerSectionSupport3s: buildHeaderSupportItems(
            context,
            "h50Text",
          ) as unknown as HomeNineHeaderSectionGeneratedProps["headerSectionSupport3s"],
          headerSectionUlsRenderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
          headerDiv0Nav0Ul0Li1Link0LinkRenderer: (_) =>
            context.signInButtonsCompact,
        },
      )}
    />
  ),
  [HeaderAppearance.Home10.key]: (props, context) => (
    <HomeTenHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeTenHeaderSectionGeneratedProps>(
        "HomeTenHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid white-logo"),
          headerDiv0Nav0Div0Link1ImageWithBasePath1ImageRenderer: (_) =>
            context.renderLogo("img-fluid normal-logo"),
          headerDiv0Nav0Div1Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Link0LinkHref: "/",
          headerDiv0Nav0Ul0Li0Link0LinkRenderer: (_) =>
            context.signInButtonsCompact,
        },
      )}
    />
  ),
  [HeaderAppearance.Home11.key]: (props, context) => {
    const bannerSetting = getBannerSectionSetting(props.pageData);
    const headerVideoLink = getHeaderFileUrl(props.setting.HeroVideoLink);
    const bannerVideoLink = getHeaderFileUrl(bannerSetting?.Video);
    const heroVideoLink = headerVideoLink ?? bannerVideoLink;
    const headerVideoImage = getHeaderFileUrl(props.setting.HeroVideoImage);
    const bannerVideoImage = getHeaderFileUrl(bannerSetting?.VideoImage);
    const heroVideoImage = headerVideoImage ?? bannerVideoImage;
    const headerVideoDisplayStatus = isActiveHeaderOption(
      props.setting.HeroVideoDisplayStatus,
    );
    const bannerVideoDisplayStatus = isActiveHeaderOption(
      bannerSetting?.VideoDisplayStatus,
    );
    const heroVideoVisible = Boolean(heroVideoLink) &&
      (headerVideoLink
        ? headerVideoDisplayStatus !== false
        : bannerVideoDisplayStatus !== false);
    const heroStatistics = props.lists?.HeroStatistics?.slice(0, 3);
    const heroAvatars = props.lists?.HeroAvatars?.length
      ? props.lists.HeroAvatars
          .map((item) => ({
            key: item.Id,
            src: getHeaderFileUrl(item.Image),
            alt: item.Image?.Name || "img",
          }))
          .filter((item): item is { key: number; src: string; alt: string } =>
            Boolean(item.src),
          )
      : undefined;
    const sideImages = props.lists?.SideImages?.length
      ? props.lists.SideImages
          .map((item) => getHeaderFileUrl(item.Image))
          .filter((url): url is string => Boolean(url))
      : undefined;

    return (
      <HomeElevenHeaderSectionGenerated
        {...buildGeneratedHeaderProps<HomeElevenHeaderSectionGeneratedProps>(
          "HomeElevenHeaderSection",
          props,
          context,
          {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          sectionDiv0Div0Div0Div0H10H1Renderer: createHeaderTitleRenderer(
            props.setting.HeroTitle,
          ),
          ...(getHeaderFileUrl(props.setting.HeroImage)
            ? {
                sectionDiv0Div0Div0Div0H10ImageWithBasePath0ImageSrc:
                  getHeaderFileUrl(props.setting.HeroImage),
              }
            : {}),
          ...(props.setting.HeroButtonText
            ? {
                sectionDiv0Div0Div0Div1Link0LinkText:
                  props.setting.HeroButtonText,
              }
            : {}),
          ...(props.setting.HeroButtonLink
            ? {
                sectionDiv0Div0Div0Div1Link0LinkHref:
                  props.setting.HeroButtonLink,
              }
            : {}),
          ...(props.setting.HeroButtonDisplayStatus
            ? {
                sectionDiv0Div0Div0Div1Link0LinkVisible:
                  isActiveHeaderOption(props.setting.HeroButtonDisplayStatus),
              }
            : {}),
          ...(heroVideoLink
            ? {
                sectionDiv0Div0Div0Div2Link0LinkHref:
                  heroVideoLink,
                sectionDiv0Div0Div0Div2Link0LinkRenderer: (
                  defaultNode: ReactNode,
                ) => (
                  <HeaderVideoLinkRenderer
                    src={heroVideoLink}
                    defaultNode={defaultNode}
                  />
                ),
              }
            : {}),
          ...(heroVideoVisible !== undefined
            ? {
                sectionDiv0Div0Div0Div2Link0LinkVisible:
                  heroVideoVisible,
              }
            : {}),
          ...(heroVideoImage
            ? {
                sectionDiv0Div0Div0Div2Link0ImageWithBasePath0ImageSrc:
                  heroVideoImage,
              }
            : {}),
          ...(props.setting.SideTitle
            ? { sectionDiv0Div0Div1Div0H40H4Text: props.setting.SideTitle }
            : {}),
          ...(props.setting.SideDescription
            ? {
                sectionDiv0Div0Div1Div0P0PText: props.setting.SideDescription,
              }
            : {}),
          ...(props.setting.SideQuote
            ? { sectionDiv0Div0Div1Div2P0PText: props.setting.SideQuote }
            : {}),
          ...(sideImages?.[0]
            ? {
                sectionDiv0Div0Div1ImageWithBasePath0ImageSrc: sideImages[0],
              }
            : {}),
          ...(sideImages?.[1]
            ? {
                sectionDiv0Div0Div1ImageWithBasePath1ImageSrc: sideImages[1],
              }
            : {}),
          ...(heroStatistics?.[0]
            ? {
                sectionDiv0Div0Div0Div2Div0Div0H20H2Text:
                  heroStatistics[0].Value,
                sectionDiv0Div0Div0Div2Div0Div0Span0SpanText:
                  heroStatistics[0].Label,
              }
            : {}),
          ...(heroStatistics?.[1]
            ? {
                sectionDiv0Div0Div0Div2Div0Div1H20H2Text:
                  heroStatistics[1].Value,
                sectionDiv0Div0Div0Div2Div0Div1Span0SpanText:
                  heroStatistics[1].Label,
              }
            : {}),
          ...(heroStatistics?.[2]
            ? {
                sectionDiv0Div0Div0Div2Div0Div2H20H2Text:
                  heroStatistics[2].Value,
                sectionDiv0Div0Div0Div2Div0Div2Span0SpanText:
                  heroStatistics[2].Label,
              }
            : {}),
          ...(heroAvatars?.length
            ? {
                headerSectionAvatarAvatarRoundeds: heroAvatars,
              }
            : {}),
          sectionHeader0Div0Nav0Div0Div0Link1ImageWithBasePath0ImageRenderer: (
            _,
          ) => context.renderLogo("img-fluid"),
          sectionHeader0Div0Nav0Div0Div1Div0Link0ImageWithBasePath0ImageRenderer:
            (_) => context.renderLogo("img-fluid"),
          sectionHeader0Div0Nav0Div0Div0Link1LinkHref: "/",
          sectionHeader0Div0Nav0Div0Div1Div0Link0LinkHref: "/",
          sectionHeader0Div0Nav0Div0Div1Div1Div2Link1LinkVisible: false,
          sectionHeader0Div0Nav0Div0Div1Div1Div2Link0LinkRenderer: (
            _defaultNode: ReactNode,
          ) => context.signInButtons,
          headerSectionSupport3s: buildHeaderSupportItems(
            context,
            "h50Text",
          ) as unknown as HomeElevenHeaderSectionGeneratedProps["headerSectionSupport3s"],
          headerSectionUlsRenderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
          sectionHeader0Div0Nav0Ul0Li2Link0LinkRenderer: (_) =>
            context.signInButtonsCompact,
          },
        )}
      />
    );
  },
  [HeaderAppearance.Home12.key]: (props, context) => (
    <HomeTwelveHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeTwelveHeaderSectionGeneratedProps>(
        "HomeTwelveHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div1Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Link0LinkHref: "/",
          headerDiv0Nav0Div1Div1Div2Link1LinkVisible: false,
          headerDiv0Nav0Div1Div1Div2Link0LinkRenderer: (
            _defaultNode: ReactNode,
          ) => context.signInButtons,
          headerSectionSupport3s: buildHeaderSupportItems(
            context,
            "h50Text",
          ) as unknown as HomeTwelveHeaderSectionGeneratedProps["headerSectionSupport3s"],
          headerSectionUlsRenderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
          headerDiv0Nav0Ul0Li0Link0LinkRenderer: (_) =>
            context.signInButtonsCompact,
        },
      )}
    />
  ),
  [HeaderAppearance.Home13.key]: (props, context) => (
    <HomeThirteenHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeThirteenHeaderSectionGeneratedProps>(
        "HomeThirteenHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseAnchor,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div1Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Link0LinkHref: "/",
          headerSectionUlsRenderer: (_) => context.signInButtons,
          headerSectionUls2Renderer: (_) => <></>,
        },
      )}
    />
  ),
  [HeaderAppearance.Home14.key]: (props, context) => (
    <HomeFourteenHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeFourteenHeaderSectionGeneratedProps>(
        "HomeFourteenHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseButton,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid white-logo"),
          headerDiv0Nav0Div0Link1ImageWithBasePath1ImageRenderer: (_) =>
            context.renderLogo("img-fluid normal-logo"),
          headerDiv0Nav0Div1Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Link0LinkHref: "/",
          headerDiv0Nav0Div1Div1Div2Link1LinkVisible: false,
          headerDiv0Nav0Div1Div1Div2Link0LinkRenderer: (
            _defaultNode: ReactNode,
          ) => context.signInButtons,
          headerSectionSupport3s: buildHeaderSupportItems(
            context,
            "h40Text",
          ) as unknown as HomeFourteenHeaderSectionGeneratedProps["headerSectionSupport3s"],
          headerSectionUlsRenderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
          headerDiv0Nav0Ul0Li0Link0LinkRenderer: (_) =>
            context.signInButtonsCompact,
        },
      )}
    />
  ),
  [HeroAppearance.Home16.key]: (props, context) => (
    <HomeSixteenHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeSixteenHeaderSectionGeneratedProps>(
        "HomeSixteenHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseButton,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid normal-logo"),
          headerDiv0Nav0Div1Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Link0LinkHref: "/",
        },
      )}
    />
  ),
  [HeroAppearance.Home17.key]: (props, context) => (
    <HomeSeventeenHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeSeventeenHeaderSectionGeneratedProps>(
        "HomeSeventeenHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseButton,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid normal-logo"),
          headerDiv0Nav0Div1Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Link0LinkHref: "/",
          headerDiv0Nav0Div1Div1Div0Link1LinkVisible: false,
          headerDiv0Nav0Div1Div1Div0Link0LinkRenderer: (_) =>
            context.signInButtons,
        },
      )}
    />
  ),
  [HeroAppearance.Home18.key]: (props, context) => (
    <HomeEighteenHeaderSectionGenerated
      {...buildGeneratedHeaderProps<HomeEighteenHeaderSectionGeneratedProps>(
        "HomeEighteenHeaderSection",
        props,
        context,
        {
          isScrolled: context.isScrolled,
          onMobileBtn: context.onMobileBtn,
          onMenuClose: context.onMenuCloseButton,
          headerDiv0Nav0Div0Link1ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid white-logo"),
          headerDiv0Nav0Div0Link1ImageWithBasePath1ImageRenderer: (_) =>
            context.renderLogo("img-fluid normal-logo"),
          headerDiv0Nav0Div1Div0Link0ImageWithBasePath0ImageRenderer: (_) =>
            context.renderLogo("img-fluid"),
          headerDiv0Nav0Div0Link1LinkHref: "/",
          headerDiv0Nav0Div1Div0Link0LinkHref: "/",
          headerDiv0Nav0Div1Div1Div2Link1LinkVisible: false,
          headerDiv0Nav0Div1Div1Div2Link0LinkRenderer: (_) =>
            context.signInButtons,
          headerSectionSupport3s: buildHeaderSupportItems(
            context,
            "h30Text",
            "i0ClassName",
          ) as unknown as HomeEighteenHeaderSectionGeneratedProps["headerSectionSupport3s"],
          headerSectionUlsRenderer: (_) =>
            renderHeaderSocialNetworks(context, { wrapWithLi: true }),
        },
      )}
    />
  ),
};

const GeneratedHeaderSection: FC<HeaderSectionProps> = (props) => {
  const { setting, pageData } = props;
  const { menuOpen, menuToggle } = useAppSetting();
  const t = useT();
  const [isScrolled, setIsScrolled] = useState(false);

  useHtmlClass(menuOpen ? "menu-opened" : []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const onMobileBtn: MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    menuToggle();
  };

  const onMenuCloseAnchor: MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    menuToggle();
  };

  const onMenuCloseButton: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    menuToggle();
  };

  const appearanceKey = setting.Appearance?.Key ?? HeaderAppearance.Home8.key;
  const n = useMemo(
    () => _.toNumber(appearanceKey.replace("Home", "")),
    [appearanceKey],
  );
  useBodyClass([
    ...(menuOpen ? ["menu-opned"] : []),
    ...(n && n != 1 ? [`theme-${n - 1}`] : []),
  ]);

  const signInButtons = <HeaderAccountActions />;
  const signInButtonsCompact = <HeaderAccountActions compact />;
  const signInHref = "/SignIn";

  // const alerts = (
  //   <CenterAlerts CenterId={pageData.CenterId} CenterSiteTemplatePageId={pageData.Id} />
  // );
  // const logo = <Image width="154" height="40" src={pageData.CommonData.ImageUrl} className="img-fluid" alt="Logo" />;
  const renderLogo = (className = "img-fluid") =>
    pageData.CommonData.Image?.Url ? (
      <SmartImage
        cover
        width={50}
        height={50}
        src={pageData.CommonData.Image.Url}
        className={className}
        alt={pageData.CommonData.Image.AltText ?? pageData.CommonData.Name}
      />
    ) : undefined;

  const logo = renderLogo();
  const logoWithName = (
    <div className="logoWithName">
      <div className="logoBox">{renderLogo()}</div>

      <h2 className="h3 logoTitle">
        <bdi dir="auto">{pageData.CommonData.Name}</bdi>
      </h2>
    </div>
  );

  if (!pageData) {
    return <></>;
    // return <HeaderSection {...props} />;
  }

  const context: HeaderRenderContext = {
    isScrolled,
    onMobileBtn,
    onMenuCloseAnchor,
    onMenuCloseButton,
    signInButtons,
    signInButtonsCompact,
    logo,
    logoWithName,
    renderLogo,
    pageData,
    contactInformationText: t(
      "landing.header.contactInformation",
      undefined,
      "Contact Information",
    ),
    followUsText: t("landing.header.followUs", undefined, "Follow Us"),
    loginText: t("common.action.login", undefined, "Login"),
    emailText: t("common.field.email", undefined, "Email"),
    mobileText: t("common.field.mobile", undefined, "Mobile"),
    contactUsText: t("landing.footer.contactUs", undefined, "Contact us"),
    signInHref,
  };

  const headerRenderers = Object.fromEntries(
    Object.entries(generatedHeaderRenderers).map(([key, generatedRenderer]) => [
      key,
      ((sectionProps: HeaderSectionProps) =>
        generatedRenderer(
          sectionProps,
          context,
        )) satisfies GeneratedSectionRenderer<HeaderSectionProps>,
    ]),
  ) as Record<string, GeneratedSectionRenderer<HeaderSectionProps>>;

  return renderGeneratedSection(
    props,
    headerRenderers,
    HeaderAppearance.Home8.key,
    (x) => x.setting.Appearance?.Key,
  );
};

export default GeneratedHeaderSection;
